import { useState, useRef } from "react";
import {
  FiBold,
  FiItalic,
  FiLink,
  FiPlus,
  FiTrash2,
  FiArrowUp,
  FiArrowDown,
  FiImage,
  FiX,
} from "react-icons/fi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MediaPicker } from "@/components/common";
import {
  displayUrl,
  EMAIL_BRAND,
  BLOCK_CONFIG,
  createBlock,
} from "./email-block-editor";
import type { BlockType, ContentBlock } from "./email-block-editor";

// ─── Block Editor Component ──────────────────────────────────────────────────

function BlockEditor({
  block,
  index,
  total,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  mediaFolder,
}: {
  block: ContentBlock;
  index: number;
  total: number;
  onChange: (updated: ContentBlock) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  mediaFolder: "campaigns" | "blog" | "general" | "all";
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  const insertAtCursor = (text: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      onChange({ ...block, content: block.content + text });
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent = block.content.substring(0, start) + text + block.content.substring(end);
    onChange({ ...block, content: newContent });
    setTimeout(() => {
      textarea.focus();
      const pos = start + text.length;
      textarea.setSelectionRange(pos, pos);
    }, 0);
  };

  const insertFormatting = (prefix: string, suffix: string, placeholder: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = block.content.substring(start, end) || placeholder;
    const newContent = block.content.substring(0, start) + prefix + selected + suffix + block.content.substring(end);
    onChange({ ...block, content: newContent });
    setTimeout(() => {
      textarea.focus();
      const pos = start + prefix.length + selected.length + suffix.length;
      textarea.setSelectionRange(pos, pos);
    }, 0);
  };

  const config = BLOCK_CONFIG[block.type];
  const Icon = config.icon;

  return (
    <div className="group relative rounded-xl border border-border bg-white hover:border-border/80 transition-colors overflow-hidden">
      {/* Block bar — type label centered + actions */}
      <div className="flex items-center px-3.5 py-1.5 border-b border-border/50">
        <div className="flex-1" />
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5 text-muted-foreground/50" />
          <span className="text-[11px] font-medium text-muted-foreground/50 uppercase tracking-wider">{config.label}</span>
        </div>
        <div className="flex-1 flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onMoveUp} disabled={index === 0} className="p-1 text-muted-foreground/40 hover:text-foreground disabled:opacity-20" title="Move up"><FiArrowUp className="w-3 h-3" /></button>
          <button onClick={onMoveDown} disabled={index === total - 1} className="p-1 text-muted-foreground/40 hover:text-foreground disabled:opacity-20" title="Move down"><FiArrowDown className="w-3 h-3" /></button>
          <button onClick={onDelete} className="p-1 text-muted-foreground/40 hover:text-red-500" title="Remove"><FiTrash2 className="w-3 h-3" /></button>
        </div>
      </div>

      {/* Block Content */}
      <div className="p-3.5">
        {block.type === "divider" && (
          <div className="py-2 text-center text-xs text-slate-400">
            <hr className="border-slate-200" />
          </div>
        )}

        {block.type === "spacer" && (
          <div className="py-1 text-center text-xs text-slate-400">Extra spacing</div>
        )}

        {block.type === "heading" && (
          <div className="flex items-center gap-2">
            <Select
              value={String(block.level || 2)}
              onValueChange={(v) => onChange({ ...block, level: parseInt(v) as 1 | 2 | 3 })}
            >
              <SelectTrigger className="w-24 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Large</SelectItem>
                <SelectItem value="2">Medium</SelectItem>
                <SelectItem value="3">Small</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={block.content}
              onChange={(e) => onChange({ ...block, content: e.target.value })}
              placeholder="Type your heading..."
              className={`h-9 flex-1 placeholder:italic placeholder:text-slate-300 ${
                block.level === 1 ? "text-lg font-bold" : block.level === 3 ? "text-sm font-semibold" : "text-base font-semibold"
              }`}
            />
          </div>
        )}

        {block.type === "text" && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1 pb-0.5">
              <button onClick={() => insertFormatting("**", "**", "bold")} className="p-1.5 text-muted-foreground/50 hover:text-foreground hover:bg-secondary rounded-md transition-colors" title="Bold"><FiBold className="w-3.5 h-3.5" /></button>
              <button onClick={() => insertFormatting("*", "*", "italic")} className="p-1.5 text-muted-foreground/50 hover:text-foreground hover:bg-secondary rounded-md transition-colors" title="Italic"><FiItalic className="w-3.5 h-3.5" /></button>
              <button onClick={() => insertFormatting("[", "](https://)", "link text")} className="p-1.5 text-muted-foreground/50 hover:text-foreground hover:bg-secondary rounded-md transition-colors" title="Insert Link"><FiLink className="w-3.5 h-3.5" /></button>
              <div className="w-px h-4 bg-border mx-1" />
              <Select onValueChange={(value) => insertAtCursor(value)}>
                <SelectTrigger className="h-7 w-auto min-w-[130px] text-xs border-slate-200">
                  <SelectValue placeholder="Insert variable..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="{{firstName}}">First Name</SelectItem>
                  <SelectItem value="{{name}}">Full Name</SelectItem>
                  <SelectItem value="{{preferredName}}">Preferred Name</SelectItem>
                  <SelectItem value="{{email}}">Email</SelectItem>
                  <SelectItem value="{{loanType}}">Loan Type</SelectItem>
                  <SelectItem value="{{years}}">Years Since Settlement</SelectItem>
                  <SelectItem value="{{allLoans}}">All Loans (list)</SelectItem>
                  <SelectItem value="{{loanCount}}">Loan Count</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea
              ref={textareaRef}
              value={block.content}
              onChange={(e) => onChange({ ...block, content: e.target.value })}
              placeholder="Type your paragraph text here..."
              rows={3}
              className="text-sm resize-none placeholder:italic placeholder:text-slate-300"
            />
          </div>
        )}

        {block.type === "list" && (
          <div>
            <p className="text-[11px] text-slate-400 mb-1.5">Type one item per line. Bullet points are added automatically.</p>
            <Textarea
              value={block.content}
              onChange={(e) => onChange({ ...block, content: e.target.value })}
              placeholder="Type your first list item here..."
              rows={3}
              className="text-sm leading-tight resize-none placeholder:italic placeholder:text-slate-300"
            />
          </div>
        )}

        {block.type === "image" && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-[11px] font-medium text-slate-500 mb-1 block">Image URL</label>
                <Input
                  value={block.url || ""}
                  onChange={(e) => onChange({ ...block, url: e.target.value })}
                  placeholder="Paste image link or browse library..."
                  className="h-9 text-sm placeholder:italic placeholder:text-slate-300"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setMediaPickerOpen(true)}
                  className="h-9 gap-1.5"
                >
                  <FiImage className="w-3.5 h-3.5" />
                  Browse
                </Button>
              </div>
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-500 mb-1 block">Alt text (optional)</label>
              <Input
                value={block.alt || ""}
                onChange={(e) => onChange({ ...block, alt: e.target.value })}
                placeholder="Describe the image..."
                className="h-9 text-sm placeholder:italic placeholder:text-slate-300"
              />
            </div>
            {block.url && (
              <div className="border rounded-lg p-2 bg-slate-50">
                <img src={displayUrl(block.url)} alt={block.alt || "Preview"} className="max-h-32 mx-auto rounded" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
              </div>
            )}
            <MediaPicker
              open={mediaPickerOpen}
              onOpenChange={setMediaPickerOpen}
              onSelect={(url, alt) => onChange({ ...block, url, alt: alt || block.alt || "" })}
              folder={mediaFolder}
              title="Choose Campaign Image"
            />
          </div>
        )}

        {block.type === "button" && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-slate-500 mb-1 block">Button label</label>
                <Input value={block.content} onChange={(e) => onChange({ ...block, content: e.target.value })} placeholder="e.g. Learn More" className="h-9 text-sm placeholder:italic placeholder:text-slate-300" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500 mb-1 block">Link URL</label>
                <Input value={block.url || ""} onChange={(e) => onChange({ ...block, url: e.target.value })} placeholder="e.g. https://..." className="h-9 text-sm placeholder:italic placeholder:text-slate-300" />
              </div>
            </div>
            {block.content && (
              <div className="text-center py-2">
                <span className="inline-block px-6 py-2 rounded text-white text-sm font-medium" style={{ backgroundColor: EMAIL_BRAND.accent }}>{block.content}</span>
              </div>
            )}
          </div>
        )}

        {block.type === "quote" && (
          <div className="space-y-2">
            <Textarea
              value={block.content}
              onChange={(e) => onChange({ ...block, content: e.target.value })}
              placeholder="Type the quote or testimonial here..."
              rows={3}
              className="text-sm resize-none placeholder:italic placeholder:text-slate-300"
            />
            <div>
              <label className="text-[11px] font-medium text-slate-500 mb-1 block">Who said it? (optional)</label>
              <Input value={block.alt || ""} onChange={(e) => onChange({ ...block, alt: e.target.value })} placeholder="e.g. Sarah M., First Home Buyer" className="h-9 text-sm placeholder:italic placeholder:text-slate-300" />
            </div>
          </div>
        )}

        {block.type === "callout" && (
          <div className="space-y-2">
            <div>
              <label className="text-[11px] font-medium text-slate-500 mb-1 block">Style</label>
              <Select value={block.alt || "info"} onValueChange={(v) => onChange({ ...block, alt: v })}>
                <SelectTrigger className="h-9 text-sm w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info (Blue)</SelectItem>
                  <SelectItem value="warning">Warning (Amber)</SelectItem>
                  <SelectItem value="tip">Tip (Green)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea
              value={block.content}
              onChange={(e) => onChange({ ...block, content: e.target.value })}
              placeholder="Type the highlighted message here..."
              rows={2}
              className="text-sm resize-none placeholder:italic placeholder:text-slate-300"
            />
          </div>
        )}

        {block.type === "video" && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-slate-500 mb-1 block">Video link</label>
                <Input value={block.url || ""} onChange={(e) => onChange({ ...block, url: e.target.value })} placeholder="e.g. https://youtube.com/..." className="h-9 text-sm placeholder:italic placeholder:text-slate-300" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500 mb-1 block">Button label</label>
                <Input value={block.content} onChange={(e) => onChange({ ...block, content: e.target.value })} placeholder="e.g. Watch Now" className="h-9 text-sm placeholder:italic placeholder:text-slate-300" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-500 mb-1 block">Thumbnail image (optional)</label>
              <div className="flex gap-2">
                <Input value={block.alt || ""} onChange={(e) => onChange({ ...block, alt: e.target.value })} placeholder="Paste thumbnail image URL..." className="h-9 text-sm placeholder:italic placeholder:text-slate-300 flex-1" />
                <Button type="button" variant="outline" size="sm" onClick={() => setMediaPickerOpen(true)} className="h-9 gap-1.5 shrink-0">
                  <FiImage className="w-3.5 h-3.5" />
                  Browse
                </Button>
              </div>
              <MediaPicker
                open={mediaPickerOpen}
                onOpenChange={setMediaPickerOpen}
                onSelect={(url) => onChange({ ...block, alt: url })}
                folder={mediaFolder}
                title="Choose Thumbnail"
              />
            </div>
          </div>
        )}

        {block.type === "signature" && (
          <div className="space-y-3">
            <Input
              value={block.content}
              onChange={(e) => onChange({ ...block, content: e.target.value })}
              placeholder="e.g. Warm regards, / Best wishes, / Kind regards,"
              className="text-sm"
            />
            <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg border border-border/50">
              <img
                src="/people/Cajetan%20Photo.JPG"
                alt="Cajetan Silva"
                className="w-11 h-11 rounded-full object-cover shrink-0"
              />
              <div className="min-w-0">
                <p className="text-sm font-bold text-foreground">Cajetan Silva</p>
                <p className="text-xs text-muted-foreground">Principal Broker</p>
                <p className="text-xs text-accent">0477 308 028 &middot; cjsilva@pathfinancial.com.au</p>
              </div>
            </div>
          </div>
        )}

        {block.type === "numbered_list" && (
          <div>
            <p className="text-[11px] text-slate-400 mb-1.5">Type one item per line. Numbers are added automatically.</p>
            <Textarea
              value={block.content}
              onChange={(e) => onChange({ ...block, content: e.target.value })}
              placeholder="Type your first list item here..."
              rows={3}
              className="text-sm leading-tight resize-none placeholder:italic placeholder:text-slate-300"
            />
          </div>
        )}

        {block.type === "two_column" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-slate-500 mb-1 block">Left column</label>
              <Textarea value={block.content} onChange={(e) => onChange({ ...block, content: e.target.value })} placeholder="Type left side content..." rows={3} className="text-sm resize-none placeholder:italic placeholder:text-slate-300" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-500 mb-1 block">Right column</label>
              <Textarea value={block.content2 || ""} onChange={(e) => onChange({ ...block, content2: e.target.value })} placeholder="Type right side content..." rows={3} className="text-sm resize-none placeholder:italic placeholder:text-slate-300" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Add Block Panel ─────────────────────────────────────────────────────────

function AddBlockPanel({ onAdd }: { onAdd: (type: BlockType) => void }) {
  const [isOpen, setIsOpen] = useState(false);

  const allTypes: BlockType[] = [
    "text", "heading", "image", "button",
    "list", "numbered_list", "quote", "callout",
    "divider", "spacer", "signature", "video", "two_column",
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full py-3.5 border-2 border-dashed border-border rounded-xl text-muted-foreground hover:border-primary/30 hover:text-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-2 text-sm font-medium"
      >
        <FiPlus className="w-4 h-4" />
        Add content block
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-foreground">Add a block</p>
        <button onClick={() => setIsOpen(false)} className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary">
          <FiX className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
        {allTypes.map((type) => {
          const cfg = BLOCK_CONFIG[type];
          const Icon = cfg.icon;
          return (
            <button
              key={type}
              onClick={() => { onAdd(type); setIsOpen(false); }}
              className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-border bg-white hover:border-primary/30 hover:shadow-sm transition-all group"
              title={cfg.description}
            >
              <Icon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
              <span className="text-[10px] font-medium text-foreground/60 leading-tight text-center">{cfg.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── BlockEditorPanel (wrapper) ──────────────────────────────────────────────

interface BlockEditorPanelProps {
  blocks: ContentBlock[];
  onBlocksChange: (blocks: ContentBlock[]) => void;
  mediaFolder?: "campaigns" | "blog" | "general" | "all";
}

export function BlockEditorPanel({ blocks, onBlocksChange, mediaFolder = "campaigns" }: BlockEditorPanelProps) {
  const updateBlock = (index: number, updated: ContentBlock) => {
    const newBlocks = [...blocks];
    newBlocks[index] = updated;
    onBlocksChange(newBlocks);
  };
  const deleteBlock = (index: number) => onBlocksChange(blocks.filter((_, i) => i !== index));
  const moveBlock = (from: number, to: number) => {
    if (to < 0 || to >= blocks.length) return;
    const newBlocks = [...blocks];
    const [moved] = newBlocks.splice(from, 1);
    newBlocks.splice(to, 0, moved);
    onBlocksChange(newBlocks);
  };
  const addBlock = (type: BlockType) => onBlocksChange([...blocks, createBlock(type)]);

  return (
    <div className="space-y-2.5">
      {blocks.map((block, index) => (
        <BlockEditor
          key={block.id}
          block={block}
          index={index}
          total={blocks.length}
          onChange={(updated) => updateBlock(index, updated)}
          onDelete={() => deleteBlock(index)}
          onMoveUp={() => moveBlock(index, index - 1)}
          onMoveDown={() => moveBlock(index, index + 1)}
          mediaFolder={mediaFolder}
        />
      ))}
      <AddBlockPanel onAdd={addBlock} />
    </div>
  );
}
